import { useTheme } from '@mui/material/styles'
import * as React from 'react'
import { FC, useCallback, useState, useEffect } from 'react'
import { useField, Field, useFormikContext } from 'formik'
import { MuiTextFieldFormik } from '@user/components/Formik'
import {
  IOption,
  IPropsWithProvider,
  OptionDependency,
  IFilterProps,
  IMultiSelectFilter,
} from '../../typings'
import CollapsibleFilter from '../atoms/CollapsibleFilter'
import { MultiSelectAutoComplete } from './MultiSelectAutoComplete'

import Grid from '@mui/material/Grid'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import MenuItem from '@mui/material/MenuItem'

interface Props extends IFilterProps, IPropsWithProvider {
  dependsOn?: OptionDependency
  hideModeSelect?: boolean
}

export const MultiSelectFilter: FC<Props> = <T,>({
  name,
  label,
  dependsOn,
  component,
  hideModeSelect = false,
  reset,
}) => {
  const path = `filters.${name}`
  const modeField = `${path}.value.mode`
  const optionField = `${path}.value.options`
  const hideModeSelectFieldPath = `${path}.hideModeSelectField`

  const [{ value: filter }] = useField<IMultiSelectFilter<T>>(path)
  const {
    propertyIsArray,
    hideModeSelectField,
    hideAllMode = false,
    value: { options },
  } = filter
  // useField helpers (like setValue) change every time the form changes
  // so use setFieldValue instead
  // https://github.com/formium/formik/issues/2268
  // https://github.com/formium/formik/issues/2858
  const { setFieldValue } = useFormikContext()

  const [dep, setDep] = useState(dependsOn)

  const setValue = useCallback(
    (values: IOption[]) => setFieldValue(optionField, values),
    [optionField, setFieldValue],
  )

  const theme = useTheme()

  useEffect(() => {
    setFieldValue(hideModeSelectFieldPath, hideModeSelect)
  }, [hideModeSelect, hideModeSelectFieldPath, setFieldValue])

  useEffect(() => {
    if (dependsOn !== undefined && dependsOn != dep) {
      setFieldValue(optionField, [])
      setDep(dependsOn)
    }
  }, [dep, dependsOn, optionField, setFieldValue])

  return (
    <CollapsibleFilter
      label={label}
      subLabel={(expanded) =>
        !expanded && options.length > 0 && `${options.length} Selected`
      }
      reset={reset}
    >
      <Grid container spacing={2}>
        {!hideModeSelectField && (
          <Grid item xs={12}>
            <Box display="flex" alignItems="center">
              <Typography color="textSecondary">Match</Typography>
              <Field
                name={modeField}
                component={MuiTextFieldFormik}
                select
                size="small"
                style={{ margin: `0 ${theme.spacing(1)}`, minWidth: '80px' }}
              >
                {propertyIsArray && !hideAllMode && (
                  <MenuItem value="all">All</MenuItem>
                )}
                <MenuItem value="any">Any</MenuItem>
                <MenuItem value="none">None</MenuItem>
              </Field>
              <Typography color="textPrimary">of the following</Typography>
            </Box>
          </Grid>
        )}

        <Grid item xs={12}>
          <MultiSelectAutoComplete
            value={options}
            setValue={setValue}
            dependsOn={dependsOn}
            component={component}
          />
        </Grid>
      </Grid>
    </CollapsibleFilter>
  )
}
